import React, { useState } from 'react';
import { X, MessageSquare, Phone, Mail, Calendar, Clock } from 'lucide-react';

const ACTIVITY_TYPES = [
  { id: 'note', label: 'Note', icon: MessageSquare, description: 'Add a general note about the applicant' },
  { id: 'interview', label: 'Interview', icon: Calendar, description: 'Schedule or record an interview' },
  { id: 'phone_call', label: 'Phone Call', icon: Phone, description: 'Log a phone conversation' },
  { id: 'email_sent', label: 'Email Sent', icon: Mail, description: 'Record an email sent to the applicant' },
];

const INTERVIEW_TYPES = [
  'Phone Screen',
  'First Interview',
  'Second Interview',
  'Technical Interview',
  'Panel Interview',
  'Final Interview',
  'Culture Fit',
];

const AddActivityModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [activityType, setActivityType] = useState('note');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    scheduledAt: '',
    interviewType: '',
    interviewers: '',
    callDuration: '',
    emailSubject: '',
    isPrivate: false,
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = {
      type: activityType,
      title: formData.title || getDefaultTitle(activityType),
      content: formData.content,
      isPrivate: formData.isPrivate,
    };

    if (activityType === 'interview') {
      data.scheduledAt = formData.scheduledAt;
      data.interviewType = formData.interviewType;
      data.interviewers = formData.interviewers ? formData.interviewers.split(',').map(s => s.trim()) : [];
    }

    if (activityType === 'phone_call' && formData.callDuration) {
      data.callDuration = parseInt(formData.callDuration) * 60; // Convert to seconds
    }

    if (activityType === 'email_sent') {
      data.emailSubject = formData.emailSubject;
    }

    onSubmit(data);
  };

  const getDefaultTitle = (type) => {
    const titles = {
      note: 'Note added',
      interview: 'Interview scheduled',
      phone_call: 'Phone call logged',
      email_sent: 'Email sent',
    };
    return titles[type] || 'Activity added';
  };

  const handleClose = () => {
    setActivityType('note');
    setFormData({
      title: '',
      content: '',
      scheduledAt: '',
      interviewType: '',
      interviewers: '',
      callDuration: '',
      emailSubject: '',
      isPrivate: false,
    });
    onClose();
  };

  const renderFormFields = () => {
    switch (activityType) {
      case 'interview':
        return (
          <>
            <div>
              <label className="form-label">Interview Type</label>
              <select
                className="form-input"
                value={formData.interviewType}
                onChange={(e) => setFormData({ ...formData, interviewType: e.target.value })}
              >
                <option value="">Select type...</option>
                {INTERVIEW_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Scheduled Date & Time</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={formData.scheduledAt}
                  onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label">Interviewers (comma separated)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., John, Sarah"
                  value={formData.interviewers}
                  onChange={(e) => setFormData({ ...formData, interviewers: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                rows="3"
                placeholder="Add interview notes or agenda..."
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              />
            </div>
          </>
        );

      case 'phone_call':
        return (
          <>
            <div>
              <label className="form-label">Call Duration (minutes)</label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="number"
                  className="form-input pl-10"
                  placeholder="e.g., 15"
                  value={formData.callDuration}
                  onChange={(e) => setFormData({ ...formData, callDuration: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="form-label">Call Notes</label>
              <textarea
                className="form-input"
                rows="4"
                placeholder="Summarize the conversation..."
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              />
            </div>
          </>
        );

      case 'email_sent':
        return (
          <>
            <div>
              <label className="form-label">Email Subject</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter email subject..."
                value={formData.emailSubject}
                onChange={(e) => setFormData({ ...formData, emailSubject: e.target.value })}
              />
            </div>
            <div>
              <label className="form-label">Email Content Summary</label>
              <textarea
                className="form-input"
                rows="4"
                placeholder="Brief summary of the email content..."
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              />
            </div>
          </>
        );

      default: // note
        return (
          <div>
            <label className="form-label">Note Content</label>
            <textarea
              className="form-input"
              rows="5"
              placeholder="Add your notes about the applicant..."
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
            />
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Add Activity</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="activity-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Activity Type Selection */}
            <div>
              <label className="form-label">Activity Type</label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {ACTIVITY_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setActivityType(type.id)}
                      className={`flex items-start p-3 rounded-lg border text-left transition-all ${
                        activityType === type.id
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                      }`}
                    >
                      <Icon size={18} className="mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-sm">{type.label}</span>
                        <p className="text-xs mt-0.5 opacity-75">{type.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title (optional for all types) */}
            <div>
              <label className="form-label">
                Title <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder={getDefaultTitle(activityType)}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            {/* Dynamic form fields based on type */}
            {renderFormFields()}

            {/* Privacy toggle */}
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.isPrivate}
                onChange={(e) => setFormData({ ...formData, isPrivate: e.target.checked })}
                className="w-4 h-4 text-purple-600 rounded"
              />
              <span className="text-gray-700">Make this activity private (admin only)</span>
            </label>
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={handleClose}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="activity-form"
            disabled={isLoading || (activityType === 'note' && !formData.content)}
            className="btn-primary disabled:opacity-50"
          >
            {isLoading ? 'Adding...' : 'Add Activity'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddActivityModal;
